import { AppContext } from "App";

import { ISectionLogger } from "@multiweb/logging";
import { ActionType, IOWObject } from "@multiweb/types/multiweb";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { FunctionUtils } from "Global/Base/Utils/FunctionUtils";
import { ErrorHandler } from "UI/Errors";
import { IKeyboardModifiersWatcher } from "UI/UI/DefaultInterface/IKeyboardModifiersWatcher";
import { FirmlyDisabledRights } from "UI/UI/Rights/FirmlyDisabledRights";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { Event, StatefulEvent } from "../../../../../Global/Base/Events";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancellationToken, CancellationTokenSource } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { DeferredStateful, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { IRightsContext } from "../../../../../Global/Base/Rights/IRightsContext";
import { IRightsManager } from "../../../../../Global/Base/Rights/IRightsManager";
import { EMPTY_ITEM_RIGHTS_COLLECTION, IItemRightsCollection, RightType } from "../../../../../Global/Base/Rights/RightTypes";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IWorkflowManager, IWorkflowMenuItem } from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../../../OW.Remoting/Remoting/RequestContext";
import { ClassItemType, IClassItem, IEvaluatedConditionResult, IRightConditionItem } from "../../../../../OW.Remoting/Remoting/Rights";
import { ActionContext } from "../../../../UI.Controls/Actions/IAction";
import { IDataRowElement } from "../../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { IDataGrid } from "../../../../UI.Controls/IDataGrid";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ComponentTree } from "../../../LogicalTree/ComponentTree";
import { ControlWrapper } from "../../../LogicalTree/ControlWrapper";
import { ComponentTreeType } from "../../../LogicalTree/Definitions";
import { ComponentRightsExtractor } from "../../../Rights/ComponentRightsExtractor";
import { RightsEvaluationOverridesType, RightsManagerType } from "../../../Rights/Definitions";
import { ControlRightTypesGroup, RightGroups } from "../../../Rights/RightGroups";
import { RightsEvaluationOverrides } from "../../../Rights/RightsEvaluationOverrides";
import { RightsManager } from "../../../Rights/RightsManager";
import { convertContextFromFrameToRequest } from "../../../Utilities/SharedUIMethods";
import { AutoRefresher } from "../../AutoRefresher";
import { FormControlWithLoggerParams, ICompositesControl, IHTMLElementWithControl, IStaticControl, IVersioned } from "../../Controls/ControlInterfaces";
import { Registrator } from "../../Controls/Registrator";
import { IMenu } from "../../FormUI/FormUI";
import { IFrameProfileInfo } from "../../IFrameColumnInfo";
import { CryptoAction } from "../Actions/CryptoAction";
import { MethodAction } from "../Actions/MethodAction";
import { ParametricFormAction } from "../Actions/ParametricFormAction";
import { WFAction } from "../Actions/WFAction";
import { WFMultiAction } from "../Actions/WFMultiAction";
import { XGAction } from "../Actions/XGAction";
import { AlignPanel } from "../AlignPanel";
import { DetailFrame } from "../DetailFrame";
import { DetailFrameRightsOverrides } from "../DetailFrameRightsOverrides";
import { IValidationMaster } from "../FormControl";
import { FrameSelChanged, HideButtons, IActualObjectChangedEventArgs, IControlWithRights } from "../Frames";
import { IForm } from "../IForm";
import { IFrame, IFrameCtxMenuData, IFrameWithRights } from "../IFrame";
import { InstanceCollection } from "../InstanceCollection";
import { ValidationManager } from "../ValidationManager";
import {
    ICompactViewMasterableFrame,
    IFrameWithKeyboardNavigation,
    IFrameWithProfile,
    IMasterableFrame,
    IMasterableFrameWithDataGrid,
    IMultiRecordsComponent,
} from "./FrameInterfaces";
import { FrameSharedMethods, IExtendedOperationsParams } from "./FrameSharedMethods";
import { IDetailFrame, IDetailMasterableFrame } from "./IDetailMasterableFrame";
import { IEditableFrame } from "./IEditableFrame";
import { MasterableFormControl } from "./MasterableFormControl";

export abstract class MasterableFrame extends MasterableFormControl implements IValidationMaster, IDetailMasterableFrame, IMultiRecordsComponent, IFrameWithRights, IFrameWithProfile, IFrameWithKeyboardNavigation, IVersioned, ICompositesControl {
    //#region Fields
    public readonly canBeMasterFrame: boolean = true;
    public frameName: string = null;
    public masterFrameName: string = null;
    public supportsMultiEditing?: boolean = null;
    public autoRefresher: AutoRefresher;
    protected _detail: DetailFrame;

    /**
     * Třídy kompozitních atributů.
     */
    public readonly composites: string;

    /* Původní(první) detailFrame. Může se hodit, pokud je otevřený modální detailForm, který se uloží do this._detail. */
    protected _originalDetail: DetailFrame;
    protected _formCanRead: boolean | undefined = undefined;
    protected _formCanInsert: boolean | undefined | null = undefined;
    protected _canDelete: boolean | undefined | null = undefined;
    protected _isEditing: boolean = false;
    protected _isMultiSelectingMode: boolean = false;
    protected _profileInfo: IFrameProfileInfo = { columns: {}, order: [] };
    protected _actualObjectStandardRights: StandardOperationRights;
    /** Uklada seznam akci, ktere se provedou po urcitych operacich (ulozeni, zruseni zmen. atd) */
    protected _afterActions: any = {};
    /**Akce lezici na seznamu*/
    protected _actions: Array<IControlWithRights> = new Array<IControlWithRights>();
    /**seznam podminek pouzitych na seznamu*/
    protected _conditionItems: Array<IRightConditionItem> = [];
    // Aktivní role uživatele
    private _activeRoleIds: Array<string>;
    protected _evaluatedConditions: IEvaluatedConditionResult[] | null = null;

    protected _suppressSaveDialog: boolean = false;

    protected _actionsList: Array<XGAction> = [];

    /**
     * Určen i na sdílení všemi potomky MaterableFrame.
     */
    protected _logger: ISectionLogger;
    protected _keyboardModifiersWatcher: IKeyboardModifiersWatcher = DIContainer.instance.create(IKeyboardModifiersWatcher);

    public get actions(): ReadonlyArray<IControlWithRights> {
        return this._actions;
    }

    private _hideButtons: HideButtons;
    protected _readVersioned: boolean | null;
    /**Pokud je seznam skryty z GUI, tak si to musim zapamatovat, aby jsem ho nikdy nezviditelnil*/
    protected _permanentInvisible: boolean = false;
    protected _actualObjectCTS?: CancellationTokenSource;
    protected _rightsUpdateCTS?: CancellationTokenSource;
    /** Určuje, zda je povoleno zobrazení akce tisk seznamu */
    /** Určuje, zda je povoleno zobrazení akcí pro graf */
    private _allowGraph: boolean;
    /** Určuje, zda je povoleno zobrazení akce tisk seznamu */
    private _allowReport: boolean;
    /** Určuje, zda má být zobrazeno menu s akcemi pro export */
    protected _allowExport: boolean;
    private _allowColumnSettings: boolean;
    protected _instances: InstanceCollection = new InstanceCollection();
    protected _componentTree: ComponentTreeType;
    protected _putAsideDetailTreeNodes: Map<IDetailFrame, ComponentTreeType> = new Map<IDetailFrame, ComponentTreeType>();
    protected readonly _workflowManager: IWorkflowManager;
    protected readonly _rightsManager: RightsManagerType;
    protected __rightsEvaluationOverrides: RightsEvaluationOverridesType;
    protected _rightsEvaluatedWhenNotInteractive: Set<RightType>;
    protected _interactive: boolean = true;
    public readonly validationManager: ValidationManager = new ValidationManager();

    /**Pri ulozeni/smazani/provedeni WF - refreshne master objeckt*/
    private readonly _masterRefresh: boolean;

    /**
     * Příznak, jestli je detail otevřen v modálním okně
     * @internal
     */
    public hasOpenedDetailInModal: boolean = false;

    /** Mapa funkcí uložených pro jednotlivá id objektů. */
    private readonly _rightsThrottledFuncMap: Map<string, () => void> = new Map<string, () => () => void>();

    /** Mapa nejnovějších kontextů uložených pro jednotlivá id objektů. */
    private readonly _rightsThrottledContextMap: Map<string, IOWObject> = new Map<string, IOWObject>();

    /** Vyčistí mapu funkcí uložených pro jednotlivá id objektů. */
    protected _clearRightsThrottledFuncMap = () => {
        this._rightsThrottledFuncMap.clear();
    };

    /** Vyčistí mapu nejnovějších kontextů uložených pro jednotlivá id objektů. */
    protected _clearRightsThrottledContextMap = () => {
        this._rightsThrottledContextMap.clear();
    };

    /** Throttled funkce _triggerRightsChecking */
    protected _evaluateAttributeChangedRightsThrottledWithContext(context: IOWObject): void {
        this._rightsThrottledContextMap.set(context.id, context);
        if (context) {
            let throttledFunc = this._rightsThrottledFuncMap.get(context.id);
            if (!throttledFunc) {
                throttledFunc = FunctionUtils.throttleFunctionBounded(() => {
                    const latestContext = this._rightsThrottledContextMap.get(context.id);
                    this._triggerRightsChecking(ControlRightTypesGroup.attributeChange, latestContext);
                }, 500);
                this._rightsThrottledFuncMap.set(context.id, throttledFunc);
            }
            throttledFunc();
        }
    }

    private _isChanged: boolean = false;
    public get isChanged(): boolean {
        return this._isChanged;
    }
    public set isChanged(value: boolean) {
        this._isChanged = value;
    }

    public get profileInfo(): IFrameProfileInfo {
        return this._profileInfo;
    }

    public set profileInfo(v: IFrameProfileInfo) {
        if (v === this._profileInfo)
            return;

        this._profileInfo = v;
        this.selectedProfileChanged.trigger(this, v);
    }

    /** Vrací, zda je povoleno zobrazení akcí pro tisk */
    public get allowReport(): boolean {
        return this._allowReport;
    }

    /** Vrací, zda je povoleno zobrazení akcí pro graf */
    public get allowGraph(): boolean {
        return this._allowGraph;
    }

    public get isMultiSelecting(): boolean {
        return this.isMultiSelectingMode;
    }
    public get canEnterMultiEdit(): boolean {
        if (this.masterFrame) {
            const mainFrame = FrameSharedMethods.findMainFrame(this);
            //pokud hlavní frame podporuje multi select, můžu do editace vstoupit pokud podporuje multieditaci a zároveň ještě není v editaci. Nebo pokud multieditaci nepodporuje vůbec
            if (mainFrame instanceof IEditableFrame && mainFrame instanceof IMultiRecordsComponent) {
                return !mainFrame.isMultiSelectingMode;//|| !mainFrame.isEditing;
            }
        }
        return true;
    }

    protected _isMultiEditing: boolean = false;
    public get isMultiEditing(): boolean {
        return this._isMultiEditing;
    }
    public set isMultiEditing(value: boolean) {
        this._isMultiEditing = value;
    }

    public get allowColumnSettings(): boolean {
        return this._allowColumnSettings;
    }
    /** Vrací, zda je povoleno zobrazení akcí pro export */
    public get allowExport(): boolean {
        return this._allowExport;
    }

    public get masterRefresh(): boolean {
        return this.masterFrame != null && this._masterRefresh;
    }

    public get isEditing(): boolean {
        return this._isEditing;
    }

    public set isEditing(v: boolean) {
        this._isEditing = v;
    }

    public get isMultiSelectingMode(): boolean {
        return this._isMultiSelectingMode;
    }

    public set isMultiSelectingMode(v: boolean) {
        if (this._isMultiSelectingMode !== v) {
            this._isMultiSelectingMode = v;
            this.selectionModeChanged.trigger(this, v);
            this._newEvaluateRightsGroup(ControlRightTypesGroup.selectionChanged);
        }
    }

    public get suppressSaveDialog(): boolean {
        return this._suppressSaveDialog;
    }

    public set hideButtons(value: HideButtons) {
        this._hideButtons = value;
    }

    public get hideButtons(): HideButtons {
        if (!this._hideButtons) {
            const aktHideButtons = FrameSharedMethods.parseHideButtons(this.ownerForm, this.element.getAttribute("data-hidebuttons"), this.isMainFrame);

            if (this.detail) {
                this._hideButtons = this.detail.hideButtons;
                this._hideButtons.refresh = aktHideButtons.refresh;
            } else
                this._hideButtons = aktHideButtons;
        }

        return this._hideButtons;
    }

    public get renderStandardRowActions(): boolean {
        return true;
    }

    public readonly uuid: string;

    /**Pole pouzivane pro nacteni prav ze serveru*/
    private _classItemsRights: IClassItem[] = [];

    /**Frame provede RefreshActualObject pri prepinani modalnich oken*/
    public refreshObjectAfterSetActive: boolean = false;
    /**Umožní refresh framu po zaktivnění formu*/
    public refreshListAfterSetActive: boolean = false;
    /**určuje, jestli je vlastníkem menu a má jej disposnout (defaultně true) */
    public isMenuOwner: boolean = true;

    public frameIsVisible: Event<IFrame, any> = new Event<IFrame, any>(); //eventy z implementovanych rozhrani
    /**
     * Událost o vzniku procesu nastavování actualObjectu
     */
    public readonly setObjectInitiated: Event<MasterableFrame, boolean> = new Event<MasterableFrame, boolean>();
    protected _isInitialized: boolean = false;
    protected _initializationStarted: boolean = false;

    protected _checkWf: boolean = undefined;

    /**
     * Resolvovat metodou this._resolveActualObjectSetDefferred.
     * Slouží pro možnost čekání na nastavení actualObjectu od jeho setteru po dokončení
     */
    protected _actualObjectSetDeferred: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();

    /**
     * Slouží pro možnost čekání na proběhnutí změny objektu už od chvíle kliknutí na záznam (přes událost onSelectionChanged)
     */
    protected _selectedRecordChangeDeferred: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();
    private _masterObject: OWObject | null = null;

    /**
     * Pro nastavení používat metodu _setActualObject!
     */
    private __actualObject: OWObject;

    protected get _actualObject(): OWObject {
        return this.__actualObject;
    }

    protected _setActualObject(object: OWObject, _isFromMasterObjChanged: boolean = false, releasePrevInstance: boolean = true): void {
        this._startActualObjectSetDeferred();

        this.__actualObject = object;

        if (releasePrevInstance) {
            this._keepUsedInstances();
        }
    }
    protected get _forceAutoSave(): boolean {
        return (this.useMasterContext || this._suppressSaveDialog);
    }

    protected _keepUsedInstances(): void {
        this._instances.keep(this.__actualObject ? [this.__actualObject] : undefined);
    }

    public get actualObject(): OWObject {
        return this._actualObject;
    }

    public set actualObject(obj: OWObject) {
        if (obj !== this.actualObject && this.masterFrame) // Pokud má tento frame masterFrame, budeme potřebovat nullovat právo vkládání, aby se načetlo znovu
            this.canInsert = null;

        this._setActualObject(obj, false);
    }

    public get isSetActualObjectRunning(): boolean {
        return !this._actualObjectSetDeferred.isResolved;
    }

    private _isMainFrame: boolean = false;

    public get isMainFrame(): boolean {
        return this._isMainFrame;
    }

    public set isMainFrame(v: boolean) {
        this._isMainFrame = v;
    }

    public get detail(): DetailFrame {
        return this._detail;
    }

    public set detail(detailFrame: DetailFrame) {
        if (this._isInitialized) {
            if (this._detail) { // MF již měl detail a nastavujeme nový
                const currentDetailComponentTree = this.detailComponentTree;
                //TODO-rites: #odebrat nebo někam odložit strom současného detailu?
                if (detailFrame) // Pokud nastavujeme nullový detail, tak nebudeme uchovávat současný
                    this._putAsideDetailTreeNodes.set(this._detail, currentDetailComponentTree);
                if (this.componentTree.component.isVirtualControl) { // Mělo by být splněno vždy, když už byl detail // TODO-rites: zpřístupnit isVirtualControl z treeNodu?
                    this.componentTree.removeChildNode(currentDetailComponentTree);
                    if (!detailFrame && this._putAsideDetailTreeNodes.has(detailFrame)) {
                        this._putAsideDetailTreeNodes.delete(detailFrame);
                    }
                }
            }

            //TODO-rites: #přidat do super-tree nodu nový strom nového detailu
            if (detailFrame) { // Nemá smysl stavět strom pro nullový detail
                //TODO-rites: #Pokud je nastavovaný mezi odloženými, nemusím ho znovu stavět
                if (this._putAsideDetailTreeNodes.has(detailFrame)) {
                    this._appendDetailTree(this._putAsideDetailTreeNodes.get(detailFrame));
                    this._putAsideDetailTreeNodes.delete(detailFrame);
                } else {
                    this._buildAndAppendTreeForDetail(detailFrame);
                }
            }
        }

        if (detailFrame === undefined && this._detail === this._originalDetail) {
            /* Pokud se ruší vazba na detail a detail je shodný s _originalDetail, tak se zruší i vazba pro _originalDetail. Vazbu je nutné rušit, jinak může ukazaovat na detail,
             * který je již disposed, což pak způsobuje problémy při vyhodnocování práv. */
            this._originalDetail = undefined;
        } else if (!this._originalDetail && detailFrame?.ownerForm === this.ownerForm) {
            this._originalDetail = detailFrame; // první nastavený detail se nastaví jako _originalDetail
        }

        this._detail = detailFrame;
    }

    public get masterObject(): OWObject {
        if (this.masterFrame) {
            return this.masterFrame.actualObject;
        }
        if (this.isMainFrame && this.ownerForm?.masterObject) {
            return this.ownerForm.masterObject;
        }
        return this._masterObject;
    }

    public set masterObject(v: OWObject) {
        this._masterObject = v;
    }

    /**
     * Zda je frame aktivni a lze s nim pracovat a poustet nad nim akce (musi byt povoleny pravy a nesmi byt ve stavu jen ke cteni)
     */
    public get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    public masterLinkToAttr: string = null;
    public masterAttr: string = null;

    public hideToolBar: boolean = false;
    public detailForm: string = null;
    public afterGuiCreateFill: string | null = null;
    public afterGuiCreateMethod: string | null = null;

    //toolbar
    private _menu: IMenu;

    /**
     * Promis s probíhajícím vyhodnocováním práv
     */
    private _rightsEvaluationChainPromise: Promise<void> = Promise.resolve();
    /**
     * Chain promisů s probihajicimi updaty dat
     */
    private _waitForUpdatesTask: Promise<void> = Promise.resolve();

    /**
     * Interní kolekce Workflow položek aktuálního objektu. Ke kolekci je potřeba přistupovat přes property workflowMenuItems.
     */
    private __workflowMenuItems: IWorkflowMenuItem[];
    protected _wfActions: Set<WFAction> = new Set<WFAction>();
    protected _wfMultiActions: Set<WFMultiAction> = new Set<WFMultiAction>();
    protected _showSubRecords: boolean = false;
    //#endregion Fields

    //#region Events
    public readonly workflowItemsChanging: Event<MasterableFrame, IWorkflowMenuItem[]> = new Event<MasterableFrame, IWorkflowMenuItem[]>();
    public readonly workflowItemsChanged: Event<MasterableFrame, IWorkflowMenuItem[]> = new Event<MasterableFrame, IWorkflowMenuItem[]>();
    private _onWorkflowItemsChangeAction: any;
    //Event pokud se na AO zmenila nejaka hodnota
    public readonly actualObjectChanged: Event<IMasterableFrame, IActualObjectChangedEventArgs> = new Event<IMasterableFrame, IActualObjectChangedEventArgs>();
    public readonly actualObjectSaved: Event<IMasterableFrame, OWObject> = new Event<IMasterableFrame, OWObject>();
    public readonly actualObjectValueChanged: Event<IFrame, OWObject> = new Event<IFrame, OWObject>();
    /**
     * Událost, která nastane v momentě, kdy frame má připravené akce pro aktuálně vybraný objekt
     */
    public readonly contextActionsReady: Event<IMasterableFrame, string> = new Event<IMasterableFrame, string>();
    public readonly initialized: StatefulEvent<IFrame, void> = new StatefulEvent<IFrame, void>();

    public readonly dataGridInitialized: Event<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>> = new Event<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>>();
    public readonly keyboardSelectionChangeInitiated: Event<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>> = new Event<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>>();

    public readonly dataLoaded: Event<MasterableFrame, any> = new Event<MasterableFrame, any>();
    public readonly dataLoadingCancelled: Event<MasterableFrame, any> = new Event<MasterableFrame, any>();
    public readonly dataLoadingFailed: Event<MasterableFrame, any> = new Event<MasterableFrame, any>();

    /**
     * Událost vyvolaná při otevírání kontextového menu ve framu
     */
    public readonly contextMenuOpening: Event<IFrame, IFrameCtxMenuData> = new Event<IFrame, IFrameCtxMenuData>();

    /**
     * Událost o změně aktuálně vybraného profilu.
     * Při změně pořadí sloupce, změně jeho viditelnosti, šířky, změně třídění nebo filtru.
     */
    public readonly frameProfileChanged: Event<IFrameWithProfile, void> = new Event<IFrameWithProfile, void>();

    /**
     * Událost o výběru jiného profilu
     */
    public readonly selectedProfileChanged: Event<IFrameWithProfile, IFrameProfileInfo> = new Event<IFrameWithProfile, IFrameProfileInfo>();

    public readonly selectionModeChanged: Event<IMasterableFrame, boolean> = new Event<IMasterableFrame, boolean>();

    //Nasledujici metody a promenne je potreba implementovat na potomcich
    public selectionChanged: Event<IMasterableFrame, FrameSelChanged> = new Event<IMasterableFrame, FrameSelChanged>();
    public readonly changesCanceled: Event<MasterableFrame, void> = new Event<MasterableFrame, void>();
    public readonly recordChangesCanceled: Event<MasterableFrame, string> = new Event<MasterableFrame, string>();
    public readonly editingRow: Event<IFrame, OWObject> = new Event<IFrame, OWObject>();

    //#endregion Events

    //#region Properties

    public get formCanRead(): boolean {
        return this._formCanRead;
    }

    public set formCanRead(val: boolean) {
        this._formCanRead = val;
    }

    public get canInsert(): boolean | undefined | null {
        return this._formCanInsert;
    }

    public set canInsert(value: boolean) {
        this._formCanInsert = value;
    }

    public get canDelete(): boolean | undefined | null {
        return this._canDelete;
    }

    public override set visible(visible: boolean) {
        if ((this.element == undefined) || this._permanentInvisible) {
            return;
        }

        this._visible = visible;
        if (visible) {
            if (this._hasContainerParent) {
                this.element.parentElement.style.display = "";
            }

            this.element.style.removeProperty("display");
            if (this instanceof ICompactViewMasterableFrame) {
                this.checkIfIsCompact();
            }
        } else {
            if (this._hasContainerParent) { // parenta chceme schovat pouze pokud je to container
                this.element.parentElement.style.display = "none";
            }

            this.element.style.display = "none";
        }

        if (!this.hideToolBar)
            this.menu?.setVisibility(this.visible);

        this.visibilityChanged.trigger(this, visible);
    }

    public override get visible(): boolean {
        return this._visible;
    }

    public get initializationStarted(): boolean {
        return this._initializationStarted;
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public get menu(): IMenu {
        return this._menu;
    }

    public set menu(value: IMenu) {
        if (this._onWorkflowItemsChangeAction != null)
            this.workflowItemsChanging.remove(this._onWorkflowItemsChangeAction);

        this._menu = value;
    }

    /**
     * Příznak, zda má frame nastaven actualObject
     * Použito při vyhodnocování práv.
     * Na ObjectListEditu je metoda přepsána, protože OLE má actualObject "vždy" - musí se tedy kontrolovat vybraný řádek.
     */
    public get hasSelectedRecord(): boolean {
        return this.actualObject != null;
    }

    public get componentTree(): ComponentTreeType {
        return this._componentTree;
    }

    public set componentTree(componentTree: ComponentTreeType) {
        this._componentTree = componentTree;
    }

    /**
     * Vrací vlastní strom tohoto framu. Pokud tento frame udržuje v nadstromu i podstrom detailu, tento vynechá.
     * @returns {}
     */
    public get ownComponentTree(): ComponentTreeType {
        if (!this.componentTree || !this.componentTree.component || !this.componentTree.component.control)
            return null;

        if (this.componentTree.component.control instanceof IMasterableFrame)
            return this.componentTree;

        return Array.from(this.componentTree.children).find(treeChildNode => treeChildNode.component.control instanceof IMasterableFrame);
    }

    public get detailComponentTree(): ComponentTreeType | null {
        if (this.detail && this.componentTree) {
            for (const childNode of this.componentTree.children) {
                if (childNode.component && childNode.component.control && childNode.component.control instanceof IDetailFrame) {
                    return childNode; // Vrátí podstrom detailu
                }
            }
        }

        return null;
    }

    public get rightsEvaluationOverrides(): RightsEvaluationOverridesType {
        return this.__rightsEvaluationOverrides;
    }

    public get rightsManager(): RightsManagerType {
        return this._rightsManager;
    }

    public get actualObjectStandardRights(): StandardOperationRights {
        if (this._actualObjectStandardRights == null) {
            //throw new InvalidOperationException("Rights hasn't been loaded yet!");
            //this._logger.warn("Rights hasn't been loaded yet!");  // TODO-rites: ~nemělo by nastat. Asi nechat tu výjimku; c2: asi nakonec ne, bude se dotazovat defaultní hodnota na rightsManageru

            this._actualObjectStandardRights = FrameSharedMethods.getDefaultIEDRights(this.ownerForm, this._activeRoleIds);
        }
        return this._actualObjectStandardRights;
    }

    /**
     * Vrací promis s probíhajícím vyhodnocováním práv, na které lze čekat
     */
    public get rightsEvaluationTask(): Promise<void> {
        return this._rightsEvaluationChainPromise;
    }

    /**
     * Položky Workflow aktuálního objektu
     */
    protected set _workflowMenuItems(items: IWorkflowMenuItem[]) {
        this.__workflowMenuItems = items;
        this.workflowItemsChanging.trigger(this, items);
        this._actualizeWFActions(items);
    }

    protected get _workflowMenuItems(): IWorkflowMenuItem[] {
        return this.__workflowMenuItems;
    }

    public override get ownerForm(): IForm {
        return super.ownerForm;
    }

    /**
     * Nastaveni ownerFormu - tj. Formu na kterem lezi MasterableFrame
     */
    public override set ownerForm(ownerForm: IForm) {
        super.ownerForm = ownerForm;
        if (this.ownerForm != null) {
            this.ownerForm.readOnlyChanged.add((_sender, e) => {
                this.interactive = !e;
                this._evaluateInteractiveRights();
            });
        }
    }

    public get readVersioned(): boolean | null {
        return this._readVersioned;
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;
        //po vyhodnocení interaktive můžeme zahodit kolekci práv, která se měla vyhodnotit při změně na interaktivní
        if (value)
            this._rightsEvaluatedWhenNotInteractive = undefined;
    }

    public get showSubRecords(): boolean {
        return this._showSubRecords;
    }

    public set showSubRecords(value: boolean) {
        this._showSubRecords = value;
    }

    //#endregion Properties

    //#region Constructors

    constructor({ logger, ...otherParams }: FormControlWithLoggerParams) {
        super(otherParams);

        /**
         * V konstruktoru framu může být jen jednoduchá inicializace hodnot (z definice a pod.)
         * Logika a asynchronní procesy musí být až v inicializační metodě (initialize()),
         * která je vyvolávaná v potřebném pořadí a dá se na její doběhnutí čekat
         */
        this.composites = this.element.getAttribute("data-composites");
        this._logger = logger;
        this._masterRefresh = DataConverter.toBoolean(this.element.getAttribute("data-masterrefresh"));
        this._readElementsAttributes();
        this._setInitialVisibility();
        this._readVersioned = this.ownerForm.readVersioned;

        this._rightsManager = DIContainer.instance.create(IRightsManager, this, this._createOrGetRightsOverrides());
        this._workflowManager = DIContainer.instance.create(IWorkflowManager);

        this._setInitialAfterGUICreateFill();
        this._setInitialDeferredValues();
        this.uuid = FrameSharedMethods.generateUuid(!!AppContext.instance.configuration.developmentMode, this.frameName);
    }

    //#Constructor Helper Methods
    private _readElementsAttributes(): void {
        this.locKey = this.element.getAttribute("data-captionlockey");
        this.objectName = this.element.getAttribute("data-objectname");
        this.masterLinkToAttr = this.element.getAttribute("data-masterlinktoattr");
        this.masterFrameName = this.element.getAttribute("data-masterframe");

        this._allowReport = DataConverter.toBoolean(this.element.getAttribute("data-printMenu"), true);
        this._allowGraph = DataConverter.toBoolean(this.element.getAttribute("data-Graph"), true);
        this._allowColumnSettings = DataConverter.toBoolean(this.element.getAttribute("data-columnSettings"), true);
        this._allowExport = DataConverter.toBoolean(this.element.getAttribute("data-exportable"));
        this._suppressSaveDialog = this.getBoolDataAttr("suppresssavedialog", false);

        this.frameName = this.element.getAttribute("data-framename");
        this.hideToolBar = this.getBoolDataAttr("hidetoolbar");
        this.afterGuiCreateFill = this.element.getAttribute("data-AfterGUICreateFill");
        this.afterGuiCreateMethod = this.element.getAttribute("data-AfterGUICreateMethod");
    }

    private _setInitialVisibility(): void {
        if (this.element.hasAttribute("data-visible")) {
            this.visible = this.getBoolDataAttr("visible");
            if (!this._visible)
                this._permanentInvisible = true;
        } else {
            this.visible = true;
        }
    }

    private _setInitialAfterGUICreateFill(): void {
        if (this.masterFrameName == null && this.ownerForm.setAttributesOnNewObject != null) {
            if (this.afterGuiCreateFill == undefined)
                this.afterGuiCreateFill = "";
            else if (!this.afterGuiCreateFill.endsWith(";"))
                this.afterGuiCreateFill += ";";

            this.afterGuiCreateFill += this.ownerForm.setAttributesOnNewObject;
        }
    }

    private _setInitialDeferredValues(): void {
        this._actualObjectSetDeferred.resolve(false);
        this._selectedRecordChangeDeferred.resolve(false);
    }
    //#endregion Constructor Helper Methods
    //#endregion Constructors

    //#region Methods

    public async initialize(): Promise<void> {
        this._buildComponentTree();
        this._rightsManager.setComponentTree(() => this._componentTree);

        await FrameSharedMethods.runWhenFirstShown(this, () => this._initializeVisual());
    }

    /**
     * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */

        // obecná inicializace frame
        await this._initializeFrame();
        // navázání listenerů
        this._initializeListeners();
        // vytvoření menu
        await this._initializeFormUI();
        // zpracování a zaregistrování akcí do menu
        await this._initializeActions();
        // inicializace prav
        await this._initializeRights();

        this._isInitialized = true;
        this.setToolbarToMultiselectMode(false);
        this.initialized.trigger(this, undefined);
    }

    protected async _initializeFrame(): Promise<void> {
        this._activeRoleIds = await OWManager.getUserRoleIDs();
    }

    protected _initializeListeners(): void {
        this._events.addListener(this.element, "focusin", () => this.element.classList.add("focused"));
        this._events.addListener(this.element, "focusout", () => this.element.classList.remove("focused"));

        if (this.isMainFrame) {
            this._events.add(this.ownerForm?.masterObjectChanged, (_, masterObject) => this._onFormMasterObjectChanged(masterObject));
            this._onFormMasterObjectChanged(this.ownerForm?.masterObject);
        }

        if (this.masterFrame) {
            this._events.add(this.masterFrame?.actualObjectSaved, async () => {
                this.canInsert = null;
                await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED);
            });
        }
    }

    protected _onFormMasterObjectChanged(formMasterObject: OWObject | null): void {
        this._events.addWithKey("formMasterObjectDataRefreshed", formMasterObject?.dataRefreshed,
            () => this._triggerRightsChecking(), { removePrevious: true });
    }

    protected abstract _initializeFormUI(): Promise<void>;

    //#region Rights
    protected _buildComponentTree(): void {
        this.__rightsEvaluationOverrides = this._createOrGetRightsOverrides();

        this._componentTree = ComponentTree.buildComponentTree(this, {
            frameContext: this.frameContext,
            rightsEvaluationOverrides: this.__rightsEvaluationOverrides,
            isPreinitialized: !!this.componentTree,
            stopTreeBuildOnNextFrame: !this.initializationStarted,
        });
        if (this.detail && this._initializationStarted) {
            this._buildAndAppendTreeForDetail(this.detail);
        }
    }

    protected _createOrGetRightsOverrides(): RightsEvaluationOverridesType {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new RightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }

    protected _appendDetailTree(detailComponentTree: ComponentTreeType): void {
        this.__buildAndAppendTreeForDetailInternal(null, detailComponentTree);
    }

    protected _buildAndAppendTreeForDetail(detailFrame: IDetailFrame): void {
        this.__buildAndAppendTreeForDetailInternal(detailFrame);
    }

    protected __buildAndAppendTreeForDetailInternal(detailFrame?: IDetailFrame, detailComponentTree?: ComponentTreeType): void {
        if (!this._checkOrCreateFramesSuperTreeRoot())
            throw new InvalidOperationException("[MF] Missing component tree!");

        if (!detailComponentTree && detailFrame)
            detailComponentTree = ComponentTree.buildComponentTree(detailFrame, {
                frameContext: this.frameContext,
                rightsEvaluationOverrides: new DetailFrameRightsOverrides(detailFrame),
                isPreinitialized: !!detailFrame.componentTree,
                stopTreeBuildOnNextFrame: true,
            }); // Vytvoří strom navázaného detailu
        this._componentTree.addNode(detailComponentTree); // Vloží strom detailu
    }

    protected _checkOrCreateFramesSuperTreeRoot(): boolean {
        if (this._componentTree) {
            if (!this._componentTree.component.isVirtualControl) {
                const newRootElement = createElement();
                const newRootPanel = new AlignPanel({
                    element: newRootElement,
                    originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
                });
                this._disposableObjects.add(newRootPanel);
                const newRootTreeNode: ComponentTree = new ComponentTree(new ControlWrapper(newRootPanel, null, false, true), null); // Vytvoří nový kořen stromu, jehož potomky budou strom MF a strom detailu
                newRootTreeNode.addNode(this._componentTree as ComponentTree); // Vloží strom MF
                this._componentTree = newRootTreeNode;
            }

            return true; // Virtuální nad-kořen již existuje/byl vytvořen
        }

        return false; // Není vůbec inicializován strom
    }

    //TODO-rites: zrevidovat, s jakými typy práv se z jakých míst volá
    protected async _newEvaluateRights(rightTypes: RightType[], priorityRightTypes?: RightType[], context: IRightsContext = this.actualObject, foreignContextEvaluation?: boolean): Promise<void> {
        const rightsEvaluationWrappedPromise = (async () => {
            await this.waitForActualObjectSet();
            if (this._isDisposed) {
                // Může nastat situace, kdy dojde k dispose masterable frame ještě před vyhodnocením práv. V takovém případě už nelze práva vyhodnocovat, protože je disposed i rights manager.
                return Promise.resolve();
            } else {
                return this._rightsManager.evaluateRights(rightTypes, context, this.isMainFrame, priorityRightTypes, foreignContextEvaluation);
            }
        })();

        //Pokud frame není interaktivní, nemá smysl vyhodnocovat práva. Práva je ale potřeba vyhodnotit, pokud se stane interaktivní
        if (!this._interactive && rightTypes) {
            let rights = rightTypes;

            if (FrameSharedMethods.isFormNameImplicit(this.detailForm)) {
                rights = rightTypes.filter(right => right !== RightType.insert);
            }

            if (this._rightsEvaluatedWhenNotInteractive) {
                this._rightsEvaluatedWhenNotInteractive = new Set([...Array.from(this._rightsEvaluatedWhenNotInteractive), ...rights]);
            } else {
                this._rightsEvaluatedWhenNotInteractive = new Set(rights);
            }

            //Pokud detail leží na jiném formu než master frame, může nastat situace, že po zavření jiného modalu se detail stane aktivní, přičemž jeho master zůstává neinteraktivní. Detail v této situaci musí vyhodnotit všechna práva která masterFrame nasbíral v režimu !interactive (třeba v reakci na masterRefresh nebo auto refresh).
            if (this.hasOpenedDetailInModal) {
                this.detail?.addRightsEvaluatedWhenNotInteractive(rights);
            }
        }
        this.enqueueRightsEvaluationTask(rightsEvaluationWrappedPromise);

        return rightsEvaluationWrappedPromise;
    }

    //TODO-rites: zrevidovat, s jakými typy práv se z jakých míst volá
    protected async _newEvaluateRightsGroup(rightGroup: ControlRightTypesGroup, context?: IRightsContext, foreignContextEvaluation?: boolean): Promise<void> {
        await this._newEvaluateRights(RightGroups.getControlRightGroupTypes(rightGroup), RightGroups.getControlPriorityRightGroupTypes(rightGroup), context, foreignContextEvaluation);
    }

    protected async _evaluateInteractiveRights(): Promise<void> {
        //pokud frame nebyl interaktivní, místo skutečného vyhodnocení práv si ukládal, která práva bude potřeba znovu vyhodnotit, až se stane interaktivní.
        const pendingRightsGroups = this._rightsEvaluatedWhenNotInteractive ? Array.from(this._rightsEvaluatedWhenNotInteractive) : undefined;
        await this._newEvaluateRights([RightType.interactive], pendingRightsGroups);
        if (this._originalDetail && this._originalDetail !== this.detail) // TODO-rites: check
            await this._originalDetail.rightsManager.evaluateRights([/*RightType.enabled, */RightType.interactive], this.actualObject, this.isMainFrame);
    }
    /**
     * Odstraní staré WF akce z menu a přidá nové
     * @param workflowMenuItems
     */
    private async _actualizeWFActions(workflowMenuItems: IWorkflowMenuItem[]): Promise<void> {
        FrameSharedMethods.clearWorkflowItems(this._wfActions, this._menu);
        if (!this.hideButtons.workflow) {
            this._wfActions = await FrameSharedMethods.createWorkflowItems(workflowMenuItems, this, this.ownerForm) as Set<WFAction>;
            this._wfActions.forEach((a) => this._menu.addAction(a));
        }
        this.workflowItemsChanged.trigger(this, workflowMenuItems);
    }
    //#endregion Rights

    /**
     * Ceka na refresh dat
     */
    public waitForDataLoad(): Promise<boolean> {
        return Promise.resolve(true);
    }

    public async waitForSelectedRecordChange(): Promise<boolean> {
        return this._selectedRecordChangeDeferred.promise;
    }

    /**
     * Počká na dokončení probíhajícího nastavení ActualObjectu na všech nadřízených framech a potom na tomto
     */
    public async waitForActualObjectSet(): Promise<boolean> {
        if (this.masterFrame)
            await this.masterFrame.waitForActualObjectSet();

        return this._actualObjectSetDeferred.promise;
    }

    /**
     * Vyčištění profilu a zrušení nastavení
     */
    public abstract clearProfileInfo(): void;

    /**
     * Ceka na dobehnuti zmen dat
     */
    public waitForPendingUpdates(): Promise<void> {
        return this._waitForUpdatesTask;
    }

    /**
     * Zaregistruje probihajici zmenu dat
     */
    public enqueuePendingUpdateTask(task: Promise<void>): Promise<void> {
        return this._waitForUpdatesTask = this._waitForUpdatesTask.then(() => task);
    }

    /**
     * Do fronty s vyhodnocováním práv přidá další ukol, který se provede, až doběhnou úkoly předchozí
     * @param task Vrací promis, na který lze čekat
     */
    public enqueueRightsEvaluationTask(task: Promise<any>): Promise<void> {
        return this._rightsEvaluationChainPromise = this._rightsEvaluationChainPromise.then(() => task);
    }

    public abstract validate(checkRecommended?: boolean, checkPostponedRequired?: boolean, validatedUnchanged?: boolean): Promise<boolean>;

    public abstract validateAndShowMessage(): Promise<boolean>;

    //Vytvoří seznam akcí a jejich práv
    protected async _initializeActions(): Promise<void> {
        // přidá podmínky z formuláře (edit, delete)
        if (this.isMainFrame) {
            // vybere z podmínek formuláře jen ty, které se opravdu využijí
            this.ownerForm.conditionItems.filter(ci => this.ownerForm.usedConditionItemsId.includes(ci.id)).forEach(ci => this._conditionItems.push(ci));
        }

        const actionElements = Array.from(this.element.querySelectorAll("[data-behaviour='action']")) as IHTMLElementWithControl[];

        if (!this.hideToolBar && actionElements.length > 0) { // Při skrytém toolbaru nemá smysl vyhodnocovat jejich práva
            let itemRightsCollection: IItemRightsCollection;

            for (const actionElement of actionElements) {
                if (!actionElement.tscControl) {
                    //pokud neni inicializovana, tak se ji pokusi znovu inicializovat
                    await Registrator.initializeControls(actionElement);
                }

                if (!actionElement.tscControl) {
                    this._logger.warn("[MasterableFrame] Unable to initialize action control", actionElement);
                    continue;
                }

                const action = actionElement.tscControl; //vytahne objekt typu action z html elementu
                if (!(action instanceof XGAction))
                    throw new InvalidOperationException(`Action id ${actionElement.id} is not an instance of XGAction`); //`

                if (this.readVersioned && (action instanceof MethodAction || action instanceof ParametricFormAction || action instanceof CryptoAction)) { // U verze žádné takové akce nemají být
                    continue;
                }

                itemRightsCollection = ComponentRightsExtractor.loadRightsForTypes(action, RightType.actionRights);

                const aktAction: IControlWithRights = { control: action, itemRightsCollection }; //todo: MV toto asi neni designove uplne spravne, ale zatim to tak nechame (do pristiho uplnku)

                if (action instanceof IStaticControl) {
                    aktAction.evaluateRightsWithoutContext = action.isStatic;
                    if (action instanceof MethodAction && action.methodName && action.methodName.length > 0) {
                        this._classItemsRights.push({ name: action.methodName, type: ClassItemType.Method });
                    }
                } else if (action.context === ActionContext.frameAction) {
                    aktAction.evaluateRightsWithoutContext = true;
                }

                action.enabled = false;
                this._actions.push(aktAction);
            }

            if (itemRightsCollection) {
                this._activeRoleIds = await OWManager.getUserRoleIDs();
                const conditionIds = new Set(ComponentRightsExtractor.getAllRightsIDs(itemRightsCollection, this._activeRoleIds));

                //Vybere ze seznamu podminek na formulari jen ty podminky, ktere jsou pouzity na seznamu a jeho komponentach
                this.ownerForm.conditionItems.filter(ci => conditionIds.has(ci.id)).forEach(ci => this._conditionItems.push(ci));
            }
        }
    }

    protected async _initializeRights(): Promise<void> {
        const firmlyDisabledRights = await FirmlyDisabledRights.get(this.objectName);
        this._canDelete = !firmlyDisabledRights.delete;

        this._buildComponentTree();

        if (this.masterFrame == null) {
            this.ownerForm.framesInitialized.add(async () => {
                await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.initialized); // TODO-rites: ~nechat tady? asi jo; c2: spouštět až po inicializaci všech framů, jinak mohlo dojít k situaci, že se na LF iniciuje vyhodnocení práv a stromem projde na podřízený detail, který ještě není inicializován a chybí mu třeba rightsManager
            });
        }
    }

    /**
     * Resolvne promise čekající na dokončení změny objektu
     * @param value Typ dokončení promise - úspěšně/neúspěšně
     */
    protected _resolveActualObjectSetDeferred(value: boolean): void {
        if (!this._actualObjectSetDeferred.isResolved)
            this._actualObjectSetDeferred.resolve(value);
    }

    /** Spustí čekání na dokončení změny objektu */
    protected _startActualObjectSetDeferred(): void {
        if (this._actualObjectSetDeferred.isResolved) {
            this._actualObjectSetDeferred = PromiseUtils.deferStateful<boolean>();
        }
    }

    /**
     * Zakazuje a povoluje tlacitka v toolbaru podle toho jestli je nebo neni v rezimu editace
     */
    public setToolbarToEditMode(_inEdit: boolean): void {
        this._newEvaluateRights([RightType.visible, RightType.enabled, RightType.editing, RightType.multiEditing]);
    }

    public setToolbarToMultiselectMode(inMultiselect: boolean): void {
        if (this.isMultiSelectingMode === inMultiselect) {
            return;
        }

        this.isMultiSelectingMode = inMultiselect;
    }

    /**
     * Nacteni OW objectu
     */
    public getActualObject(objectId: string, _forceLoad = false, objectName: string = this.objectName, ct?: CancellationToken): Promise<OWObject> {
        if (objectId === "0") {
            return new Promise<OWObject>((r) => {
                r(this.actualObject);
                this.selectionChanged.trigger(this, { sender: this, objectName: objectName, actualObject: this.actualObject });
            });
        }

        return OWManager.getObject(objectId, objectName, undefined, undefined, this.readVersioned, this._getRequestContext(ct)).then((obj) => {
            this.actualObject = obj;

            return obj;
        }).catch((err) => {
            ErrorHandler.instance.handleError(err);
            return err;
        });
    }

    /**
     * Zruší nastavený actualObject čímž vyčistí detail a vyvolá událost selectionChanged
     */
    public clearActualObject(): void {
        this.actualObject = null;
        this.selectionChanged.trigger(this, { sender: this, objectName: this.objectName, actualObject: this.actualObject });
        this._newEvaluateRightsGroup(ControlRightTypesGroup.selectionChanged);
    }

    public abstract deleteRecord(maintainSelection?: boolean): Promise<boolean>;

    public abstract editRecord(): Promise<boolean>;

    /**
     * Založí nový záznam
     * @param objectName Název třídy, jejíž záznam se má založit
     * @param skipCheckSaveOnMaster Pokud je nastaveno na true, neprovede se kontrola uložení objektů na nadřízeném framu. Např. pokud byl formulář otevřen v openMode=insert, nemá smysl hned volat kontrolu uložení.
     */
    public abstract newRecord(objectName?: string, skipCheckSaveOnMaster?: boolean): Promise<boolean>;

    public abstract prevRow(): void;

    public abstract nextRow(): void;

    /**
     * Vytvoří kopii záznamu
     * @param propagateError na true znamená, že chyby si ošetří volající metoda (např. na DetailFramu)
     */
    public abstract createCopy(propagateError?: boolean): Promise<boolean>;

    /**
     * Navratove hodnoty:
     * null - ulozeni probehlo v poradku
     * prazdne pole - ulozeni se nepodarilo, ale neni co zpracovat
     * naplnene pole - vraci vyjimky, vznikle pri ukladani
     * @param _id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     * @param _checkRecommended Pokud je vyplněno, tak validuje i doporučená pole
     * @param _checkPostponed Pokud je vyplněno, tak validuje i povinně odložená pole
     * @param _skipValidation Pokud je true, tak klient nebude validovat pole
     * @param _isFormClosing Příznak zda-li se zavírá formulář
     * @param _skipSavingEditContextId Příznak zdali se má ukládat editační kontext
     */
    public async saveRecords(_id?: string, _checkRecommended?: boolean, _checkPostponed?: boolean, _skipValidation?: boolean, _isFormClosing?: boolean, _skipSavingEditContextId?: boolean): Promise<SaveResultData[] | null> {
        this._clearRightsThrottledFuncMap();
        this._clearRightsThrottledContextMap();
        return null;
    }

    /** Spustí refresh na všech prvcích framu (seznam, grupování, ...) */
    public abstract refreshOnClick(): Promise<void>;

    public async cancelChanges(_maintainSelection: boolean = false): Promise<void> {
        this.validationManager.clearErrors();
        this.setToolbarToEditMode(false);
        await this._doMasterRefresh();
        this.isChanged = false;
        this._clearRightsThrottledFuncMap();
        this._clearRightsThrottledContextMap();
        this.changesCanceled.trigger(this, undefined);

        await this.rollbackContext();
    }

    protected _actualObjectHasWF: boolean;

    public async triggerActualObjectChange(actualObject: OWObject, previousActualObject: OWObject): Promise<void> {
        // při události actualObjectChanged se počítá s tím, že wf se už vyhodnotilo
        this._actualObjectHasWF = await actualObject?.hasWf();

        this.actualObjectChanged.trigger(this, { actualObject: actualObject, previousActualObject: previousActualObject });

        let groupToEvaluate = ControlRightTypesGroup.selectionChanged;
        if (this.isEditing)
            groupToEvaluate = groupToEvaluate | ControlRightTypesGroup.inEdit;

        if ((actualObject?.objectName ?? this.objectName) !== previousActualObject?.objectName) {
            groupToEvaluate = groupToEvaluate | ControlRightTypesGroup.IED;
        }
        //Pokud má frame detail, počká se s vyhodnocením práv, až bude mít nasetovaný nový actualObject
        await this.detail?.waitForActualObjectSet();
        return this._triggerRightsChecking(groupToEvaluate).catch(PromiseUtils.cancellationCatch);
    }

    protected async _triggerRightsChecking(rightGroup: ControlRightTypesGroup = ControlRightTypesGroup.IED, context: IOWObject = this.actualObject): Promise<void> {
        try {
            // pokud nějaké komponenty vyhodnocují práva, tak počkáme na jejich doběhnutí
            await this.rightsEvaluationTask;
            // potom vyhodnotíme svoje práva
            if (context && !context.isClientSideEncrypted && context.classInfo.encryptingAllowed) {
                await this.enqueueRightsEvaluationTask(this._newEvaluateRights(RightGroups.getControlRightGroupTypes(rightGroup), [RightType.useEncryption, RightType.useDecryption, RightType.useDigitalSign], context));
            } else {
                await this.enqueueRightsEvaluationTask(this._newEvaluateRightsGroup(rightGroup, context, context !== this.actualObject));
            }
            if (context && context === this.actualObject) {
                // Vyhodnocení workflow
                if (this.actualObject.isNew)
                    this._workflowMenuItems = []; //spouští changing a následně changed
                else {
                    const prevID = this.actualObject.id;
                    await this.enqueueRightsEvaluationTask(this._getWFItems());
                    if (prevID != this.actualObject.id) { // Pokud během vyhodnocování dojde ke změně actualObjectu, tak není možné s novým pokračovat tam, kde se skončilo s předchozím
                        return;
                    }
                }

                this._triggerContextActionsReadyWithRunningEvaluationsCheck();
            } else if (!context) {
                this.contextActionsReady.trigger(this, null);
            }
        } catch (e) {
            this._logger.error("Error when triggering rights evaluation", e);
        }
    }

    /** Spuštění události contextActionsReady. Událost se vyvolá, až po doběhnutí probíhajících vyhodnocení práv. */
    private _triggerContextActionsReadyWithRunningEvaluationsCheck(): void {
        if (this.rightsManager.runningEvaluationsCount > 0) {
            const rightsEvaluatedListener = (_sender: RightsManager, _arg: IRightsContext): void => {
                if (this.rightsManager.runningEvaluationsCount === 0) {
                    this.rightsManager.rightsEvaluated.remove(rightsEvaluatedListener);
                    this.contextActionsReady.trigger(this, this.actualObject.id);
                }
            };
            this.rightsManager.rightsEvaluated.add(rightsEvaluatedListener);
        } else
            this.contextActionsReady.trigger(this, this.actualObject.id);
    }

    public triggerActualObjectValueChange(actualObject: OWObject): void {
        this.actualObjectValueChanged.trigger(this, actualObject);

        this.isChanged = true;
    }

    protected async _getWFItems(): Promise<IWorkflowMenuItem[]> {
        if (this.readVersioned || this.hideButtons.workflow) { // U verzovaných záznamů žádné WF akce nejsou
            this._checkWf = true;
            this._workflowMenuItems = null;
            return null;
        }

        let items: IWorkflowMenuItem[] = null;
        const wfItemsResult = await this._workflowManager.getWFItems(this.actualObject, this._checkWf, this._getRequestContext()).catch((err) => {
            ErrorHandler.instance.handleError(err);
            return null;
        });
        if (wfItemsResult != null) {
            items = wfItemsResult.workflowMenuItems;
            this._checkWf = wfItemsResult.checkWorkflow;
        }
        this._workflowMenuItems = items;

        if (items != null) {
            if (items.find(item => item.ValidatePostReq)) {
                await this._newEvaluateRights([RightType.postponedRequired]);
            }
        }

        return items;
    }

    /**
     * Znovu načte položky WF pro actualObject.
     */
    public async reloadWFItems(): Promise<void> {
        if (this.actualObject != null) {
            await this._getWFItems();
        }
    }

    public getWorkflowMenuItems(): IWorkflowMenuItem[] {
        return this._workflowMenuItems;
    }

    protected _clearWorkflow(): void {
        this._workflowMenuItems = [];
    }

    /**
     * Znovunacteni dat do seznamu
     * @param _manualRefresh - jestli je akce vyvolana uzivatelem (zabranuje nebo povoluje oznaceni radku v seznamu)
     */
    public refreshData(_manualRefresh: boolean): Promise<any> {
        throw new Error("Method RefreshData not implemented");
    }

    // TODO-rites: přejmenovat; nechat v metodě, volá se v potomcích
    /**
     * Vyhodnocuje práva formuláře (editace, vložení, mazání)
     * @param ct
     */
    public async actualizeRights(): Promise<void> {
        await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED); //TODO-rites: ?? chceme to tu, když to v podstatě spouští vyhodnocení nad nadřízeným framem? nebude stačit že si to nadřízený vyřídí sám? A tento DF by potom přebíral výsledek?
    }

    /**
     * Zkontroluje, zda se má udělat refresh objektu i na master framu a případně ho provede
     */
    protected async _doMasterRefresh(): Promise<void> {
        if (this.masterFrame != null && this.masterRefresh) {
            await this.masterFrame.refreshActualObject();
        } else {
            FrameSharedMethods.refreshFrameAfterSetActive(this.ownerForm);
        }
    }

    /** Aktualizuje vybraný aplikační objekt daty z perzistentního úložiště a zkontroluje, zda je nutné provést refresh mastera */
    public async refreshActualObject(): Promise<void> {
        if (this.masterRefresh || this.ownerForm.frameToRefreshAfterSetFormActive) {
            await this._doMasterRefresh();
        }

        if (this.actualObject) {
            await this._reloadActualObjectOnly();
        }
    }

    /**
     * Aktualizuje vybraný aplikační objekt aniž by bral v potaz MasterRefresh
     */
    protected async _reloadActualObjectOnly(): Promise<void> {
        if (this.actualObject != undefined)
            this.validationManager.clearErrors(this.actualObject.id);

        await this.actualObject.reload();

        this.selectionChanged.trigger(this, { sender: this, objectName: this.objectName, actualObject: this.actualObject });
        await this.triggerActualObjectChange(this.actualObject, this.actualObject);
    }

    public async checkIfIsCompact(): Promise<void> {
        // do nothing.
    }

    protected _createExtendedOperationsParams(): IExtendedOperationsParams {
        return {
            ownerForm: this.ownerForm,
            masterFrame: this.masterFrame,
            afterGuiCreateMethod: this.afterGuiCreateMethod,
            afterGuiCreateFill: this.afterGuiCreateFill,
            masterLinkToAttr: this.masterLinkToAttr,
            masterAttr: this.masterAttr,
            isMainFrame: this.isMainFrame,
            showSubrecordsActive: this.showSubRecords,
        };
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this.frameContext, cancellationToken);
    }

    /**
     * Vrací, jestli tlačítko "Předchozí" nebo "Následující" na DetailForm může být aktivní
     * @param _actionType Typ akce
     * @returns Tlačítko může být aktivní
     */
    public canBeEnabled(_actionType: ActionType): boolean {
        return true;
    }
    //#endregion Methods

    //#region IDisposable
    //destruktor
    public override dispose(): void {
        if (this._isDisposed) {
            return;
        }
        super.dispose();

        this.selectionModeChanged.removeAll();
        this.selectionChanged.removeAll();
        this.changesCanceled.removeAll();
        this.recordChangesCanceled.removeAll();
        this.editingRow.removeAll();
        this.actualObjectChanged.removeAll();
        this.rightsManager.dispose();
        this.actualObjectSaved.removeAll();
        this.actualObjectValueChanged.removeAll();
        this.initialized.removeAll();
        this.dataLoaded.removeAll();
        this.dataLoadingCancelled.removeAll();
        this.dataLoadingFailed.removeAll();
        this.frameIsVisible = null;
        this.workflowItemsChanging.removeAll();
        this.workflowItemsChanged.removeAll();
        this.contextActionsReady.removeAll();

        if (this.isMenuOwner) {
            this.menu?.dispose();
        }

        this._instances.dispose();
    }

    //#endregion IDisposable
}
